import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  describeOffer,
  fetchOfferPreview,
  maskingModeLabel,
  EXCHANGE_TIMOCOM,
  type OfferPreview as PreviewData,
  type VehicleProfile,
} from './publicationsApi';

/**
 * Podgląd oferty przed wystawieniem.
 *
 * „System przygotowuje, człowiek zatwierdza" — a zatwierdzanie w ciemno nie
 * jest zatwierdzaniem. Do tej pory operator klikał „Wystaw", nie widząc ani
 * zamaskowanej lokalizacji, ani masy, ani braków; te ostatnie wychodziły
 * dopiero w kolejce, minutę później, jako błąd publikacji.
 *
 * Treść składa **serwer tą samą funkcją, której używa wysyłka** — podgląd
 * zbudowany po stronie frontu rozjechałby się z ofertą przy pierwszej
 * zmianie reguł i byłby gorszy niż jego brak.
 */
const formatAddress = (a: { city: string; postalCode?: string } | undefined) =>
  a ? [a.postalCode, a.city].filter(Boolean).join(' ') : '—';

export const OfferPreview = ({
  userOfferId,
  mode,
  exchange = EXCHANGE_TIMOCOM,
  vehicle,
}: {
  userOfferId: number | string;
  mode?: string;
  exchange?: string;
  /** Pojazd wybrany w oknie publikacji; bez niego serwer bierze typ zlecenia. */
  vehicle?: VehicleProfile;
}) => {
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const show = async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      setPreview(await fetchOfferPreview(userOfferId, mode, exchange, vehicle));
    } catch {
      setErrorMessage('Nie udało się pobrać podglądu oferty — spróbuj ponownie.');
    } finally {
      setBusy(false);
    }
  };

  if (preview === null) {
    return (
      <Box sx={{ my: 1 }}>
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => void show()}
          disabled={busy}
        >
          Pokaż podgląd oferty
        </Button>
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    );
  }

  if (!preview.ok) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {preview.reason ?? 'Nie można przygotować oferty.'}
        </Typography>
        {preview.braki.length > 0 && (
          <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
            {preview.braki.map((b) => (
              <li key={b}>
                <Typography variant="caption">{b}</Typography>
              </li>
            ))}
          </ul>
        )}
      </Alert>
    );
  }

  const offer = describeOffer(preview.oferta);

  return (
    <Box
      sx={{
        my: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        <Typography variant="subtitle2">Tak zobaczy to przewoźnik</Typography>
        <Chip size="small" label={maskingModeLabel(preview.tryb)} />
      </Stack>
      <Typography variant="body2">
        <strong>Załadunek:</strong> {formatAddress(offer?.loading?.address)}{' '}
        {offer?.loading?.when}
      </Typography>
      <Typography variant="body2">
        <strong>Rozładunek:</strong> {formatAddress(offer?.unloading?.address)}{' '}
        {offer?.unloading?.when}
      </Typography>
      <Typography variant="body2">
        <strong>Ładunek:</strong> {offer?.cargo}
      </Typography>
      {preview.zalozenia && preview.zalozenia.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Założenia oferty:
          </Typography>
          <ul style={{ margin: '2px 0 0 18px', padding: 0 }}>
            {preview.zalozenia.map((z) => (
              <li key={z}>
                <Typography variant="caption" color="text.secondary">
                  {z}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
};
